import React, { memo, useState } from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/react-hooks";
import {
    CardValidity,
    InvoiceFragment,
    NetsOperation,
    NetsProcessResponseFragment,
    NetsTransactionFragment,
    NetsTransactionsQuery,
    NetsTransactionsQueryVariables,
    NetsTransactionType,
} from "../generated/gql/graphql";
import { Link, useLocation } from "react-router-dom";
import {
    Box,
    CircularProgress,
    Collapse,
    IconButton,
    Pagination,
    PaginationItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { usePageTitle } from "../util/page_title";
import { formatCustomerId, formatTicketId } from "../util/formatting";
import { NETS_PROCESS_RESPONSE_FRAGMENT, NetsResponseCode } from "../widgets/NetsResponseCode";
import { TimestampText } from "../widgets/date_time";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import { buildFlagIcon } from "./FrontPage";
import WarningIcon from "@mui/icons-material/Warning";
import PublicIcon from "@mui/icons-material/Public";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { sortBy } from "lodash";
import { JsonDiagnosticButton } from "../widgets/diagnostics";
import { ModeNight, SecurityUpdateGood } from "@mui/icons-material";

const amountColumnWidth = "100px";

export const TRANSACTION_TYPE_LABELS: Record<NetsTransactionType, string> = {
    [NetsTransactionType.AppPreAuth]: "APP: Kort reservasjon",
    [NetsTransactionType.AppInitial]: "APP: Utestående beløp betalt med nytt kort",
    [NetsTransactionType.AppSubsequent]: "APP: Utestående beløp betalt med eksisterende kort",
    [NetsTransactionType.MitNightly]: "MIT: Daglig forbruk betalt med eksisterende kort",
    [NetsTransactionType.MitRetry]: "MIT: Utestående beløp betalt uten 3D-secure/app-autentisering",
};

export const NETS_TRANSACTION_FRAGMENT = gql`
    fragment NetsTransaction on NetsTransaction {
        id
        netsTransactionId
        type
        savedCard {
            id
            customer {
                id
                name
                phoneNumberFormatted
            }
            transactionId {
                transactionId
            }
            displayName
            cardDetails {
                maskedPan
                issuer
                issuerCountry
            }
            validity
        }

        credits {
            id
        }

        createdTime
        processedSuccessfullyTime
        amount
        amountRegistered
        amountCaptured
        captureResponse {
            ...NetsProcessResponse
        }

        invoices {
            ...Invoice
        }
        invoicesDelegated {
            ...Invoice
        }
        credits {
            id

            creditResponse {
                ...NetsProcessResponse
            }
            amount
            amountCredited
            invoices {
                ...Invoice
            }
            createdTime
            processedTime
        }
    }

    fragment Invoice on Invoice {
        invoiceNumber
        ticket {
            id
            createdTime
            date
        }
        totalAmount
        complaint {
            id
            createdTime
        }
        createdTime
    }

    ${NETS_PROCESS_RESPONSE_FRAGMENT}
`;

const NETS_TRANSACTIONS_QUERY = gql`
    query NetsTransactions($query: String!, $page: Int!, $limit: Int!) {
        netsTransactions(query: $query, page: $page, limit: $limit) {
            totalPages
            result {
                ...NetsTransaction
            }
        }
    }
    ${NETS_TRANSACTION_FRAGMENT}
`;

export function formatMoney(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return "–";
    if (amount < 0) return "- " + formatMoney(-amount as number);

    if (amount % 100 === 0) return amount / 100 + " kr";
    return (amount / 100).toFixed(2) + " kr";
}

export function formatMoneyShort(amount: number | null | undefined): string {
    return formatMoney(amount);
}

export const NetsTransactionsListPage = memo(() => {
    usePageTitle("Kortbetalinger");
    const [searchQuery, setSearchQuery] = useState("");
    const basePath = "/nets-transactions";

    const location = useLocation();
    const locationQuery = new URLSearchParams(location.search);
    const page = parseInt(locationQuery.get("page") || "0", 10) || 1;
    const query = useQuery<NetsTransactionsQuery, NetsTransactionsQueryVariables>(NETS_TRANSACTIONS_QUERY, {
        variables: {
            query: searchQuery,
            page: page - 1,
            limit: 40,
        },
    });
    const transactions = (query.data || query.previousData)?.netsTransactions;

    return (
        <div style={{ width: "100%", height: "100%", overflowY: "scroll" }}>
            <div style={{ width: "100%", maxWidth: "1600px", margin: "0 auto" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <div
                        style={{
                            visibility: query.loading ? "visible" : "hidden",
                        }}
                    >
                        <CircularProgress />
                    </div>

                    <div style={{ width: "400px", margin: "40px 20px" }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label={"Søk etter app-ID, mobilnummer, transaksjons-id eller ordre-nummer"}
                            variant={"filled"}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <NetsTransactionsTable showCardInfo={true} transactions={transactions?.result || []} />

                <div
                    style={{
                        margin: "0 auto",
                        width: "max-content",
                        marginTop: "30px",
                    }}
                >
                    <Pagination
                        count={transactions?.totalPages || 1}
                        page={page}
                        renderItem={(item) => (
                            <PaginationItem component={Link} to={`${basePath}/?page=${item.page}`} {...item} />
                        )}
                    />
                </div>
            </div>
        </div>
    );
});

type NetsTransactionRow = NetsTransactionFragment;

export function NetsTransactionsTable(props: {
    showCardInfo: boolean;
    transactions: NetsTransactionsQuery["netsTransactions"]["result"];
}) {
    return (
        <TableContainer component={Paper}>
            <Table size="small" sx={{ minWidth: 700 }} aria-label="simple table">
                <TableHead>
                    <TableRow>
                        <TableCell />
                        <TableCell>Kvitterings-ID</TableCell>
                        <TableCell>Nets Transaction ID</TableCell>
                        {props.showCardInfo ? (
                            <React.Fragment>
                                <TableCell>App-Id</TableCell>
                                <TableCell>Mobilnummer</TableCell>
                                <TableCell>Kortnummer</TableCell>
                            </React.Fragment>
                        ) : null}{" "}
                        <TableCell align="right" width={amountColumnWidth}>
                            <Typography variant={"caption"}>Beløp (kr)</Typography>
                            <br />
                            Registrert
                        </TableCell>
                        <TableCell align="right" width={amountColumnWidth}>
                            <Typography variant={"caption"}>Beløp (kr)</Typography>
                            <br />
                            Trukket
                        </TableCell>
                        <TableCell>Nets Respons kode</TableCell>
                        <TableCell>Tidspunkt</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {props.transactions.map((t) => {
                        const transaction = t as NetsTransactionRow;
                        return (
                            <TransactionRow
                                key={transaction.id}
                                showCardInfo={props.showCardInfo}
                                transaction={transaction}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function TransactionRow({ showCardInfo, transaction }: { showCardInfo: boolean; transaction: NetsTransactionRow }) {
    const [open, setOpen] = React.useState(false);
    const customer = transaction.savedCard.customer;
    const invoices = sortBy(transaction.invoices, "invoiceNumber") as unknown as ReadonlyArray<InvoiceFragment>;
    return (
        <React.Fragment>
            <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Link to={"/nets-transaction/" + transaction.id}>{transaction.id}</Link>
                </TableCell>
                <TableCell>
                    <Link to={"/nets-transaction/" + transaction.id}>{transaction.netsTransactionId}</Link>
                </TableCell>
                {showCardInfo ? (
                    <React.Fragment>
                        <TableCell>
                            <Link to={"/customer/" + customer.id}>{formatCustomerId(customer.id)}</Link>
                        </TableCell>
                        <TableCell>
                            {(customer.phoneNumberFormatted || "") + (customer.name ? " (" + customer.name + ")" : "")}
                        </TableCell>
                        <TableCell align={"right"}>{transaction.savedCard.displayName}</TableCell>
                    </React.Fragment>
                ) : null}
                <TableCell align={"right"}>{formatMoney(transaction.amountRegistered)}</TableCell>
                <TableCell align={"right"}>
                    {transaction.processedSuccessfullyTime || transaction.amountCaptured != 0
                        ? formatMoney(transaction.amountCaptured)
                        : null}
                </TableCell>
                <TableCell>
                    <NetsResponseCode
                        operation={NetsOperation.Capture}
                        response={transaction.captureResponse as NetsProcessResponseFragment}
                    />
                </TableCell>
                <TableCell>
                    <TimestampText value={transaction.createdTime} />
                </TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                    <JsonDiagnosticButton name={"transaction"} value={transaction} />
                    {buildFlagIcon(
                        transaction.type === NetsTransactionType.AppInitial ||
                            transaction.type === NetsTransactionType.AppSubsequent,
                        <SecurityUpdateGood />,
                        "Beløpet var godkjent av kunden i en app-betaling",
                    )}{" "}
                    {buildFlagIcon(
                        transaction.type === NetsTransactionType.MitNightly,
                        <ModeNight />,
                        "Trekket ble gjort som del av nattlig betalingsjobb",
                    )}
                    {buildFlagIcon(
                        transaction.amountCaptured == 0 && transaction.savedCard.validity != CardValidity.Active,
                        <WarningIcon />,
                        "Brukeren har byttet kort etter denne betalingen",
                    )}
                    {buildFlagIcon(transaction.credits.length > 0, <CreditCardOffIcon />, "Kredittert")}
                    {buildFlagIcon(
                        transaction.savedCard.cardDetails?.issuerCountry != "NO",
                        <PublicIcon />,
                        "Utstederland: " + transaction.savedCard.cardDetails?.issuerCountry,
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                                Billetter
                            </Typography>
                            <Table width={"auto"}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Løpenummer</TableCell>
                                        <TableCell>Billett-Id</TableCell>
                                        <TableCell>Kjøpt</TableCell>
                                        <TableCell>Faktura endret dato</TableCell>
                                        <TableCell align={"right"}>Beløp</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {invoices.map((invoice) => (
                                        <TableRow key={invoice.invoiceNumber}>
                                            <TableCell>{invoice.invoiceNumber}</TableCell>
                                            <TableCell>
                                                <Link to={"/ticket/" + invoice.ticket.id}>
                                                    {formatTicketId(invoice.ticket.id)}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                <TimestampText value={invoice.ticket.createdTime} />
                                            </TableCell>
                                            <TableCell>
                                                <TimestampText value={invoice.createdTime} />
                                            </TableCell>
                                            <TableCell align={"right"}>{formatMoney(invoice.totalAmount)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {transaction.credits.map((credit) => {
                                        return credit.invoices.map((i) => {
                                            const invoice = i as InvoiceFragment;
                                            return (
                                                <TableRow key={credit.id}>
                                                    <TableCell>{invoice.invoiceNumber}</TableCell>
                                                    <TableCell>
                                                        <Link to={"/ticket/" + invoice.ticket.id}>
                                                            {formatTicketId(invoice.ticket.id)}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TimestampText value={invoice.ticket.createdTime} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TimestampText value={invoice.createdTime} />
                                                    </TableCell>
                                                    <TableCell align={"right"}>
                                                        {formatMoney(invoice.totalAmount)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <NetsResponseCode
                                                            operation={NetsOperation.Credit}
                                                            response={
                                                                credit.creditResponse as NetsProcessResponseFragment
                                                            }
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        });
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}
